import { useEffect, useState } from 'react';
import ReplyPost from './replyPost';
import Thread from './thread';
import { FormData, IForumService, PlayerInfo, Post } from '../model/interfaces';
import { container } from 'tsyringe';
import { useAsyncData } from '@utils/hooks';
import { Loader } from '@ui-kit/components/loader';
import { useParams } from 'react-router-dom';
import TopicHeader from './topicHeader';
import styles from '../styles/topicsList.module.scss';
import FormWithEditor from './formWithEditor';
import { ResponseCode } from '@base/utils/constants';
import { t, useBehaviorSubject, useSubject } from '@base/utils/helpers';
import { IMessage } from '@base/features/message/message';

function PostsList() {
  const forumService: IForumService = container.resolve('forumService');
  const message: IMessage = container.resolve('message');
  const { topicId } = useParams<{ topicId: string }>();
  const { loading } = useAsyncData(() => forumService.getPostsList(Number(topicId)), topicId);
  const postsData = useSubject(forumService.postsList);
  const isModer = useBehaviorSubject(forumService.isModerating);
  const [creatingPost, setCreatingPost] = useState(false);

  const [isLoading, setLoading] = useState(false);
  const [uniquePosters, setUniquePosters] = useState<Record<string, PlayerInfo>>({});

  useEffect(() => {
    (async function () {
      try {
        if (postsData?.posts) {
          setLoading(true);
          const uniquePostersArr = await Promise.all(
            [
              ...new Set([...postsData.posts.map((post) => post.UserID), postsData.thread.UserID]),
            ].map((cur: string, i: number) => forumService.getPosterInfo(cur, postsData.forum.ForumID))
          );

          const uniquePostersHash = uniquePostersArr.reduce(
            (map: Record<string, PlayerInfo>, obj: PlayerInfo) => ((map[obj.userId] = obj), map),
            {}
          );
          setUniquePosters(uniquePostersHash);
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [postsData?.forum.ForumID, postsData?.posts, forumService]);

  const submitCreatePost = async (data: FormData) => {
    setCreatingPost(true);
    forumService
      .createMessage(Number(topicId), data.message, data.subject)
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          message.success(res.message! ? res.message! : `Post created`, () =>
            forumService.getPostsList(Number(topicId))
          );
        } else {
          message.warning(res.message!);
        }
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => setCreatingPost(false));
  };

  if (!postsData) return null;
  if (loading || isLoading) return <Loader />;

  return (
    <>
      <TopicHeader
        title={postsData.thread.Topic}
        onClick={() => document.getElementById('forumSubjectInput')?.focus()}
      />
      <div className={styles.mainBody}>
        <Thread
          thread={postsData.thread}
          creator={uniquePosters[postsData.thread.UserID]}
          refreshList={() => forumService.getPostsList(Number(topicId))}
        />

        {postsData.posts &&
          postsData.posts.map((post: Post, id: number) => {
            if (!isModer && Object.prototype.hasOwnProperty.call(post, 'Hidden')) return null;
            return (
              <ReplyPost
                key={id}
                post={post}
                poster={uniquePosters[post.UserID]}
                refreshList={() => forumService.getPostsList(Number(topicId))}
              />
            );
          })}
        {!isModer && postsData.thread.State === 'L' ? null : (
          <FormWithEditor
            titleForm={t("reply")}
            loading={creatingPost}
            placeholderInput={t("subject")}
            placeholderEditor={t("your_messsage")}
            buttonTitle={t("reply")}
            submit={submitCreatePost}
          />
        )}
      </div>
    </>
  );
}

export default PostsList;
